import { Controller } from "@hotwired/stimulus"
import ScriptedVideoChannel from "../../channels/scripted_video_channel"

const PIPELINE_STEPS = [
  "Writing script",
  "Analyzing script",
  "Detecting audio mode",
  "Generating reference images",
  "Generating first frames",
  "Generating scene videos",
  "Generating background music",
  "Generating narration",
  "Assembling video"
]

// Script tab controller — word/char count updates + ActionCable subscriptions
export default class extends Controller {
  static targets = [
    "wordCount", "charCount", "durationEstimate",
    "analysisSpinner", "analysisStatus",
    "analyzeButton", "analyzeLabel",
    "pipelineProgress", "pipelineStep", "pipelineBar"
  ]
  static values = { configId: String }

  connect() {
    if (this.hasConfigIdValue && this.configIdValue) {
      // Guard against duplicate subscriptions on Turbo re-navigation
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      this.subscribeToChannel()
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  updateCounts(event) {
    const text = event.target.value || ""
    const words = text.split(/\s+/).filter(w => w.length > 0).length
    const chars = text.length
    const durationSecs = Math.round(chars / 12.5)
    const minutes = Math.floor(durationSecs / 60)
    const secs = String(durationSecs % 60).padStart(2, "0")

    if (this.hasWordCountTarget) this.wordCountTarget.textContent = `${words} words`
    if (this.hasCharCountTarget) this.charCountTarget.textContent = `${chars} chars`
    if (this.hasDurationEstimateTarget) {
      this.durationEstimateTarget.textContent = `Est. duration: ${minutes}:${secs}`
    }
  }

  // Submit analyze request via fetch so the page stays put and spinner remains visible
  async analyzeScript(event) {
    const url = event.currentTarget.dataset.url
    if (!url) return

    this.showAnalysisSpinner("Analyzing script...")
    this.disableAnalyzeButton()

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Request failed (${response.status})`)
      }
      // Job is enqueued — spinner stays visible, ActionCable will deliver updates
    } catch (error) {
      this.hideAnalysisSpinner()
      this.enableAnalyzeButton()
      this.showAnalysisError(error.message || "Failed to start analysis")
    }
  }

  // --- Private ---

  subscribeToChannel() {
    this.subscription = ScriptedVideoChannel.subscribe(this.configIdValue, {
      onAnalysisUpdate: (data) => this.handleAnalysisUpdate(data),
      onAutoGenerateProgress: (data) => this.handlePipelineProgress(data)
    })
  }

  handleAnalysisUpdate(data) {
    switch (data.status) {
      case "analyzing":
        this.showAnalysisSpinner("Analyzing script...")
        this.disableAnalyzeButton()
        break
      case "completed":
        this.hideAnalysisSpinner()
        this.enableAnalyzeButton()
        this.turboReload()
        break
      case "failed":
        this.hideAnalysisSpinner()
        this.enableAnalyzeButton()
        this.showAnalysisError(data.error || "Analysis failed")
        break
    }
  }

  handlePipelineProgress(data) {
    if (this.hasPipelineProgressTarget) {
      this.pipelineProgressTarget.classList.remove("hidden")
    }

    if (this.hasPipelineStepTarget) {
      const stepLabel = PIPELINE_STEPS[data.step_index] || data.step
      const statusSuffix = data.status === "completed" ? " (done)" : ""
      this.pipelineStepTarget.textContent = `${stepLabel}${statusSuffix} (${data.step_index + 1}/${data.total_steps})`
    }

    if (this.hasPipelineBarTarget) {
      const pct = ((data.step_index + (data.status === "completed" ? 1 : 0.5)) / data.total_steps) * 100
      this.pipelineBarTarget.style.width = `${Math.min(pct, 100)}%`
    }

    // All steps done — reload
    if (data.status === "completed" && data.step_index === data.total_steps - 1) {
      setTimeout(() => this.turboReload(), 1000)
    }

    // Pipeline failed
    if (data.status === "failed") {
      if (this.hasPipelineStepTarget) {
        this.pipelineStepTarget.textContent = `Failed: ${data.error || data.step}`
        this.pipelineStepTarget.classList.add("text-red-600")
        this.pipelineStepTarget.classList.remove("text-gray-600")
      }
    }
  }

  showAnalysisSpinner(message) {
    if (this.hasAnalysisSpinnerTarget) this.analysisSpinnerTarget.classList.remove("hidden")
    if (this.hasAnalysisStatusTarget) {
      this.analysisStatusTarget.classList.remove("hidden")
      this.analysisStatusTarget.textContent = message
    }
  }

  hideAnalysisSpinner() {
    if (this.hasAnalysisSpinnerTarget) this.analysisSpinnerTarget.classList.add("hidden")
    if (this.hasAnalysisStatusTarget) this.analysisStatusTarget.classList.add("hidden")
  }

  showAnalysisError(message) {
    if (this.hasAnalysisStatusTarget) {
      this.analysisStatusTarget.classList.remove("hidden")
      this.analysisStatusTarget.textContent = message
      this.analysisStatusTarget.classList.add("text-red-600")
      this.analysisStatusTarget.classList.remove("text-gray-500")
    }
  }

  disableAnalyzeButton() {
    if (this.hasAnalyzeButtonTarget) {
      this.analyzeButtonTarget.disabled = true
      this.analyzeButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasAnalyzeLabelTarget) {
      this.analyzeLabelTarget.textContent = "Analyzing..."
    }
  }

  enableAnalyzeButton() {
    if (this.hasAnalyzeButtonTarget) {
      this.analyzeButtonTarget.disabled = false
      this.analyzeButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasAnalyzeLabelTarget) {
      this.analyzeLabelTarget.textContent = "Analyze Script"
    }
  }

  /** Turbo-compatible page reload */
  turboReload() {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
